// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, useCurrentUser, PhoneInput, apiErrorMessage, fmtDuration } from "./common";
import { api, orgContactTransfer } from "../api";
import { usePermissionsFromSession } from "../components/permissions";
import { Modal, Field } from "../components/Modal";
import { DeletionConfirmModal } from "../components/DeletionConfirmModal";
import { Pager } from "../components/Pager";
import { OrganizationHistoryModal } from "../components/OrganizationHistoryModal";
import { OrganizationMergeModal } from "../components/OrganizationMergeModal";
import { LocationPicker } from "../components/naver/LocationPicker";
import { formatDate } from "../components/timeFormat";
import { TagsInput } from "../components/TagsInput";
import { useSaveToast } from "../components/SaveToast";

// 표 화면용 — 카드 그리드보다 한 페이지에 더 담아도 자연스럽다.
const PER_PAGE = 20;

// 권역 화면과 동일한 "방금 전 / N분 전 / N시간 전 / 절대 날짜" 표시. 작은 헬퍼라
// 양쪽에 따로 두는 게 추상화 비용보다 가독성 좋음.
function formatRefreshedAt(ts) {
  if (!ts) return "아직 집계 안 됨";
  const t = new Date(ts);
  const diffMin = Math.round((Date.now() - t.getTime()) / 60000);
  if (diffMin < 1)    return "방금 전";
  if (diffMin < 60)   return `${diffMin}분 전`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}시간 전`;
  return t.toLocaleString("ko-KR", { dateStyle: "short", timeStyle: "short" });
}

// 평균 응답시간 단위 변환은 공용 fmtDuration(분→시간→일→달)에 위임.
// 단, 이 화면은 0/없음을 '·'가 아니라 '-'로 표시한다.
function formatResponseTime(mins) {
  if (!mins || mins <= 0) return '-';
  return fmtDuration(mins);
}

// 사업 예산(원 단위)을 억/천만 단위 한글로. 하한·상한이 다르면 범위로 보여준다.
// (예산은 남양주 통합돌봄처럼 "사업비 8억~25억" 식으로 폭을 가진 값이라 range 표기.)
function formatBudgetWon(won) {
  const n = Number(won);
  if (!n || n <= 0) return null;
  if (n >= 100000000) {
    const eok = n / 100000000;
    return `${eok % 1 === 0 ? eok : eok.toFixed(1)}억원`;
  }
  return `${Math.round(n / 10000).toLocaleString("ko-KR")}만원`;
}
function formatBudgetRange(min, max) {
  const lo = formatBudgetWon(min), hi = formatBudgetWon(max);
  if (lo && hi) return lo === hi ? lo : `${lo} ~ ${hi}`;
  return lo || hi || '-';
}

const EMPTY_ORG = {
  name: "", category: "hospital", address: "", phone: "", emergency_phone: "",
  contact_name: "", contact_title: "", contact_age: "", primary_contact_user_id: null,
  contract_status: "none",
  case_count: 0, avg_response_mins: 0, services: "",
  region_id: "", // 빈 문자열 = 권역 미지정 (admins 화면과 동일 sentinel)
  latitude: null, longitude: null,
};

// 권역 미지정 sentinel — backend로 보낼 때 빈 문자열 → null로 변환.
const REGION_NONE = "";

// Operator-editable field sets per modal mode. case_count and
// avg_response_mins are excluded — those are operational stats derived
// from real activity, not policy fields.
const NEW_FIELDS  = ["name", "category", "address", "phone", "contact_name", "contact_title", "contact_age", "region_id", "latitude", "longitude"];
const EDIT_FIELDS = ["name", "category", "address", "phone", "emergency_phone",
                     "contact_name", "contact_title", "contact_age", "primary_contact_user_id",
                     "contract_status",
                     "services", "region_id", "latitude", "longitude"];

function pick(obj, keys) { return keys.reduce((a, k) => { a[k] = obj[k]; return a; }, {}); }

// 담당자(o.contact) 표시 한 줄: "이름 · 직책". 계정 연결이면 ✓계정 배지.
function VerifiedContactBadge() {
  return (
    <span title="실제 계정과 연결된 담당자"
      style={{fontSize:9, fontWeight:700, color:'#1F855A', background:'#E3F5EC',
              borderRadius:999, padding:'1px 6px', whiteSpace:'nowrap'}}>✓계정</span>
  );
}
function contactLine(c) {
  if (!c || !c.name) return null;
  return `${c.name}${c.title ? ' · ' + c.title : ''}`;
}
function ContactInline({ contact }) {
  if (!contact || !contact.name) return <>-</>;
  return (
    <span style={{display:'inline-flex', alignItems:'center', gap:6, flexWrap:'wrap'}}>
      {contactLine(contact)}
      {contact.verified && <VerifiedContactBadge/>}
    </span>
  );
}

// 담당자 편집 — 연결 가능한 계정(candidates)이 있으면 '대표 담당자' 셀렉트로 실제
// 계정과 연결한다. '직접 입력'을 고르거나 후보가 없으면 기존 자유 텍스트로 폴백한다.
// 나이(contact_age)는 계정에 없는 기관 고유값이라 항상 자유 입력으로 둔다.
function ContactEditor({ form, set, candidates }) {
  const hasCands = Array.isArray(candidates) && candidates.length > 0;
  const pcid = form.primary_contact_user_id ?? null;
  const direct = pcid == null;
  return (
    <>
      {hasCands && (
        <Field label="대표 담당자">
          <select className="select" value={direct ? "" : String(pcid)}
            onChange={e => { const v = e.target.value; set('primary_contact_user_id')(v === "" ? null : Number(v)); }}>
            {candidates.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.job_title ? ` (${c.job_title})` : ''}{c.state !== 'active' ? ' · 비활성' : ''}
              </option>
            ))}
            <option value="">직접 입력…</option>
          </select>
        </Field>
      )}
      {(!hasCands || direct) && (
        <>
          <Field label="담당자 이름"><input className="input" value={form.contact_name||''} onChange={e=>set('contact_name')(e.target.value)}/></Field>
          <Field label="담당자 직책"><input className="input" value={form.contact_title||''} onChange={e=>set('contact_title')(e.target.value)}/></Field>
        </>
      )}
      <Field label="담당자 나이"><input className="input" type="number" min={18} max={100} value={form.contact_age||''} onChange={e=>set('contact_age')(e.target.value)}/></Field>
    </>
  );
}

function humanizeError(e) {
  return apiErrorMessage(e);
}

// 유관기관 연결 계정 해제/이관 모달. 기관에 소속된 계정(대표 담당자 포함, 그 외 agency
// 계정도) 중 하나를 골라 해제(기관에서 분리)하거나 다른 기관(타 권역 포함)으로 이관한다.
// direct=true(시스템 관리자)면 즉시 처리, false(권역 관리자)면 승인 큐에 적재되어
// system_admin 승인 시 반영. 계정별 진행 중(pending) 신청이 있으면 권역 관리자에겐 먼저 노출.
function OrgContactTransferModal({ org, direct, onClose, onDone }) {
  const [mode, setMode] = React.useState("release"); // "release" | "transfer"
  const [reason, setReason] = React.useState("");
  const [opts, setOpts] = React.useState(null);
  const [accountId, setAccountId] = React.useState(null); // 대상 계정
  const [q, setQ] = React.useState("");
  const [targets, setTargets] = React.useState([]);
  const [targetId, setTargetId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  // 마운트 시 연결 계정 목록 + 계정별 진행 중 신청 로드.
  React.useEffect(() => {
    let live = true;
    orgContactTransfer.options(org.id)
      .then(d => {
        if (!live) return;
        setOpts(d);
        const accts = d.accounts || [];
        // 기본 선택: 대표 담당자 우선, 없으면 첫 계정.
        const primary = accts.find(a => a.is_primary);
        setAccountId(primary ? primary.id : (accts[0] ? accts[0].id : null));
      })
      .catch(e => { if (live) setErr(humanizeError(e)); });
    return () => { live = false; };
  }, [org.id]);

  // 이관 대상 기관 검색(디바운스). q가 비면 후보 비움.
  React.useEffect(() => {
    if (mode !== "transfer") return;
    const term = q.trim();
    if (!term) { setTargets([]); return; }
    let live = true;
    const t = setTimeout(() => {
      orgContactTransfer.options(org.id, term)
        .then(d => { if (live) setTargets(d.target_organizations || []); })
        .catch(() => { if (live) setTargets([]); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, mode, org.id]);

  const loaded   = !!opts;
  const accounts = opts?.accounts || [];
  const account  = accounts.find(a => a.id === accountId);
  const pending  = account?.pending;
  const target   = targets.find(t => t.id === targetId);
  const hasAccounts = accounts.length > 0;

  // 권역 관리자(승인 큐)는 진행 중 신청을 먼저 보여주고 폼을 막는다. 시스템 관리자
  // (즉시 처리)는 pending이 있어도 직접 처리하면 갈음되므로 폼을 그대로 노출한다.
  const blockForPending = !direct && !!pending;

  const submit = async () => {
    setErr(null);
    if (!accountId) { setErr("대상 계정을 선택해주세요."); return; }
    if (!reason.trim()) { setErr(direct ? "처리 사유를 입력해주세요." : "신청 사유를 입력해주세요."); return; }
    if (mode === "transfer" && !targetId) { setErr("이관할 대상 기관을 선택해주세요."); return; }
    setBusy(true);
    try {
      await orgContactTransfer.request(org.id, accountId, mode, reason.trim(), mode === "transfer" ? targetId : undefined);
      onDone(mode);
    } catch (e) {
      setErr(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`${org.name} · 계정 해제·이관${direct ? "" : " 신청"}`}
      onClose={onClose}
      width={520}
      footer={(!loaded || !hasAccounts || blockForPending) ? (
        <button className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
      ) : (
        <>
          <button className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button className="btn btn-primary btn-md" onClick={submit} disabled={busy}>
            {busy ? (direct ? "처리 중…" : "신청 중…") : (direct ? "처리" : "신청")}
          </button>
        </>
      )}
    >
      {err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{err}</div>}

      {!loaded ? (
        <div className="muted" style={{fontSize:12.5, padding:'8px 2px'}}>불러오는 중…</div>
      ) : !hasAccounts ? (
        <div style={{background:'var(--bg-sub)', padding:'12px 14px', borderRadius:10, fontSize:12.5, lineHeight:1.6, color:'var(--ink-2)'}}>
          이 기관에 연결된 사용자 계정이 없습니다. 해제·이관할 대상이 없어요.
        </div>
      ) : (
        <>
          {/* 대상 계정 선택 — 기관에 연결된 비매니저 계정들 */}
          <Field label="대상 계정">
            {accounts.length === 1 ? (
              <div style={{background:'var(--bg-sub)', borderRadius:8, padding:'10px 12px', fontSize:13, fontWeight:600}}>
                {account?.name}{account?.job_title ? <span className="muted" style={{fontWeight:500}}> · {account.job_title}</span> : null}
                {account?.is_primary && <span className="badge badge-violet" style={{marginLeft:6, fontSize:10}}>대표</span>}
              </div>
            ) : (
              <select className="select" value={accountId ?? ""} onChange={e=>setAccountId(Number(e.target.value))}>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.job_title ? ` (${a.job_title})` : ''}{a.is_primary ? ' · 대표' : ''}{a.state !== 'active' ? ' · 비활성' : ''}{a.pending ? ' · 검토중' : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {blockForPending ? (
            <div style={{background:'var(--amber-50)', color:'var(--amber-700, var(--ink-2))', padding:'12px 14px', borderRadius:10, fontSize:12.5, lineHeight:1.6}}>
              이 계정은 이미 검토 중인 신청이 있습니다 — {pending.action === 'transfer'
                ? `${pending.to_organization_name || '대상 기관'}(으)로 이관`
                : '해제'}.<br/>
              승인 또는 반려 후 다시 신청할 수 있습니다.
            </div>
          ) : (
          <>
          {/* 동작 선택 */}
          <Field label={direct ? "처리 종류" : "신청 종류"}>
            <div style={{display:'flex', gap:8}}>
              {[{k:'release', l:'해제(기관에서 분리)'}, {k:'transfer', l:'다른 기관 이관'}].map(o => (
                <button key={o.k} type="button"
                  className={"btn btn-md " + (mode === o.k ? "btn-primary" : "btn-ghost")}
                  style={{flex:1}} onClick={()=>setMode(o.k)}>
                  {o.l}
                </button>
              ))}
            </div>
          </Field>

          {mode === 'transfer' && (
            <Field label="이관할 기관">
              <input className="input" placeholder="기관명·주소로 검색" value={q} onChange={e=>setQ(e.target.value)}/>
              {target && (
                <div style={{marginTop:6, fontSize:12, color:'var(--ink-2)'}}>
                  선택됨: <b>{target.name}</b>{target.region_name ? ` · ${target.region_name}` : ''}
                </div>
              )}
              {q.trim() && targets.length > 0 && (
                <div style={{marginTop:6, maxHeight:180, overflowY:'auto', border:'1px solid var(--border-soft)', borderRadius:8}}>
                  {targets.map(t => (
                    <div key={t.id} onClick={()=>setTargetId(t.id)}
                      style={{padding:'8px 12px', cursor:'pointer', fontSize:12.5,
                              background: t.id === targetId ? 'var(--violet-50)' : 'transparent',
                              borderBottom:'1px solid var(--border-soft)'}}>
                      <div style={{fontWeight:600}}>{t.name}</div>
                      <div className="muted" style={{fontSize:11}}>{CAT_LABEL[t.category] || t.category}{t.region_name ? ` · ${t.region_name}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
              {q.trim() && targets.length === 0 && (
                <div className="muted" style={{marginTop:6, fontSize:12}}>검색 결과가 없습니다.</div>
              )}
            </Field>
          )}

          <Field label={direct ? "처리 사유" : "신청 사유"}>
            <textarea className="input" rows={3} value={reason} onChange={e=>setReason(e.target.value)}
              placeholder={mode === 'transfer' ? '예) 담당자 인사이동으로 소속 기관 변경 필요' : '예) 담당자 퇴직으로 담당 해제 필요'}/>
          </Field>

          <div className="muted" style={{fontSize:11.5, lineHeight:1.6}}>
            {direct ? "이 작업은 즉시 반영됩니다. " : "신청은 시스템 관리자 승인 후 반영됩니다. "}
            {mode === 'transfer'
              ? "이관 시 계정의 소속 기관이 대상 기관으로 함께 이동합니다(대상 기관에 대표 담당자가 없으면 대표로 지정됩니다)."
              : "해제 시 계정의 기관 소속이 함께 해제됩니다(이후 기관 삭제 가능)."}
          </div>
          </>
          )}
        </>
      )}
    </Modal>
  );
}

const CAT_LABEL = { hospital: "병원", health: "보건", fire: "소방", welfare: "복지", care: "요양" };
const CAT_COLOR = { hospital: "rose", health: "green", fire: "orange", welfare: "sky", care: "amber" };
const CONTRACT_LABEL = { completed: "완료", reviewing: "검토중", linked: "연동중", none: "미체결" };
const CONTRACT_COLOR = { completed: "green", reviewing: "amber", linked: "green", none: "rose" };
// 소속 케어매니저 현황 셀 — management.tsx의 STATUS_LABEL/COLOR와 동일 라벨 유지.
const MGR_STATUS_LABEL = { available: "가용", busy: "바쁨", overloaded: "과부하", moving: "이동중", offline: "오프라인" };
const MGR_STATUS_COLOR = { available: "green", busy: "amber", overloaded: "rose", moving: "amber", offline: "gray" };

const SORT_OPTIONS = [
  { key: "id",            label: "등록 순",      cmp: (a,b)=> a.id - b.id },
  { key: "name",          label: "이름 순",      cmp: (a,b)=> a.name.localeCompare(b.name, 'ko') },
  { key: "category",      label: "카테고리 순",  cmp: (a,b)=> a.category.localeCompare(b.category) },
  { key: "case_desc",     label: "케이스 많은 순", cmp: (a,b)=> b.case_count - a.case_count },
  { key: "response_asc",  label: "응답 빠른 순", cmp: (a,b)=> (a.avg_response_mins||999) - (b.avg_response_mins||999) },
];

function OrgManagement() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  // 권역 고정 역할: 서버 scoped_orgs 가 자기 권역으로 강제하는 region_admin.
  // 이 역할은 항상 한 권역만 보므로 (1) 어느 권역인지 제목 옆 칩으로 표시하고
  // (2) 동작하지 않는(서버가 덮어쓰는) 권역 필터는 숨긴다. system_admin 등 전역
  // 역할은 region_id 가 없어 false → 기존처럼 전 권역 + 필터 그대로.
  const regionLocked = !!user?.region_id && user?.role === "region_admin";
  // 병합·일괄작업·통계 재집계는 전역/교차권역 작업이라 system_admin 전용(백엔드도
  // require_system_admin!). region_admin은 등록/편집/삭제만 가능하므로 이 affordance들을
  // 숨겨 "UI엔 보이는데 API 403" 불일치를 막는다. 로딩 플래시 동안은 낙관적으로 노출.
  const isSystemAdmin = !perms.loaded || perms.role === "system_admin";
  const [orgs, setOrgs] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // 모달 셀렉트용 권역 목록. 마운트 시 1회. 실패해도 셀렉트가 "미지정"만 노출되어
  // 모달이 깨지지 않는다 (admins 화면과 동일 패턴).
  React.useEffect(() => {
    fetch("/api/v1/regions", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(list => setRegions(Array.isArray(list) ? list : []))
      .catch(() => setRegions([]));
  }, []);
  const [selectedId, setSelectedId] = React.useState(() => {
    // Honour ?id=N so other screens can deep-link to a specific org row.
    const raw = new URLSearchParams(window.location.search).get("id");
    return raw ? Number(raw) : null;
  });
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_ORG);
  const [candidates, setCandidates] = React.useState([]); // 대표 담당자 후보 계정
  const [err, setErr] = React.useState(null);
  const { notifySaved, saveToast } = useSaveToast();
  const [q, setQ] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("all");
  const [contractFilter, setContractFilter] = React.useState("all");
  // 권역 필터: "all" | "none"(권역 미지정) | region_id 문자열. admins 어휘와 동일.
  const [regionFilter, setRegionFilter] = React.useState("all");
  // 좌표(위경도) 보유 기관만 — 지도에 표시 가능한 기관만 추리는 토글. 서버 geo=1.
  // 기본 끔: 이 화면은 "권역에 등록된 유관기관 명부"라 기본값은 전수여야 한다.
  // 기본 켬이던 시절에는 좌표 없는 기관(주로 복지관)이 통째로 빠진 채 화면 총계가
  // 권역 총계인 것처럼 보여, 대시보드의 기관 수와 계속 어긋났다(예: 587 vs 712).
  const [geoOnly, setGeoOnly] = React.useState(false);
  // 대시보드 서비스 카탈로그 카드에서 ?service_code=LTC-01(&service_label=방문요양)로
  // 들어오면 "이 급여를 제공하는 기관"만 본다. 화면에는 해제 가능한 칩으로 노출한다.
  const [serviceFilter, setServiceFilter] = React.useState(() => {
    const qs = new URLSearchParams(window.location.search);
    const code = qs.get("service_code");
    return code ? { code, label: qs.get("service_label") || code } : null;
  });
  const [sortKey, setSortKey] = React.useState("id");
  const [deletePrompt, setDeletePrompt] = React.useState(null);
  // 운영 지표 이력 모달 — null일 때 닫힘, organization 객체일 때 열림.
  const [historyOrg, setHistoryOrg] = React.useState(null);
  // 병합 모달 — source 기관 객체 또는 null.
  const [mergeSource, setMergeSource] = React.useState(null);
  // 담당자 해제·이관 신청 모달 — 대상 기관 객체 또는 null.
  const [contactXfer, setContactXfer] = React.useState(null);
  // 페이지네이션: page는 서버에 전송, envelope에서 pagination 객체 수신.
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  // 카드 카운트용 — 서버가 facet으로 계산해서 보냄(by_category는 카테고리 필터 제외).
  // recent_by_category: 최근 N일 내 신규 등록 수(카테고리별). 트렌드 칩에 사용.
  const [counts, setCounts] = React.useState({
    by_category: {}, recent_by_category: {}, recent_window_days: 30,
    by_contract: {}, total: 0
  });

  // Server-side q/category/contract/region are applied; client-side sort
  // happens after fetch on the current page.
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q)                          params.set("q", q);
      if (catFilter      !== "all")   params.set("category", catFilter);
      if (contractFilter !== "all")   params.set("contract_status", contractFilter);
      if (regionFilter   !== "all")   params.set("region_id", regionFilter);
      if (geoOnly)                    params.set("geo", "1");
      if (serviceFilter)              params.set("service_code", serviceFilter.code);
      params.set("page", String(page));
      params.set("per_page", String(PER_PAGE));
      const r = await fetch(`/api/v1/organizations?${params.toString()}`, { credentials: "same-origin" });
      if (!r.ok) throw new Error(`오류 (${r.status})`);
      const data = await r.json();
      if (Array.isArray(data)) {
        // bare array 응답 (page 미전송 시) — 방어적으로 수용.
        // counts는 클라이언트에서 응급 계산. 정상 흐름이면 도달하지 않음.
        setOrgs(data);
        setPagination({ page: 1, total: data.length, total_pages: 1 });
        const byCat = {};
        for (const o of data) byCat[o.category] = (byCat[o.category] || 0) + 1;
        setCounts({ by_category: byCat, by_contract: {}, total: data.length });
      } else {
        setOrgs(data.organizations || []);
        setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
        setCounts(data.counts || { by_category: {}, by_contract: {}, total: 0 });
      }
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setLoading(false); }
  }, [q, catFilter, contractFilter, regionFilter, geoOnly, serviceFilter, page]);
  React.useEffect(() => { load(); }, [load]);

  // 필터 변경 시 1페이지로 자동 복귀(빈 페이지 방지). selectedId는 유지 —
  // deep-link 시나리오를 깨지 않게.
  React.useEffect(() => { setPage(1); }, [q, catFilter, contractFilter, regionFilter, geoOnly, serviceFilter]);
  // 페이지 범위 초과(필터로 결과가 줄어든 경우) 보정.
  React.useEffect(() => {
    if (page > pagination.total_pages && pagination.total_pages > 0) setPage(1);
  }, [page, pagination.total_pages]);

  const visible = React.useMemo(() => {
    const cmp = SORT_OPTIONS.find(s => s.key === sortKey)?.cmp;
    return cmp ? [...orgs].sort(cmp) : orgs;
  }, [orgs, sortKey]);

  // 선택 행 결정 규칙:
  //   1) 사용자가 클릭한 selectedId가 현재 페이지에 있으면 그 항목
  //   2) selectedId가 없으면(첫 진입) visible[0] 자동 선택해 상세 노출
  //   3) selectedId가 있는데 페이지/필터로 보이지 않으면 → 우측 패널에 명확한 안내.
  //      visible[0]로 silent fallback하지 않음(엉뚱한 행 데이터 노출 방지).
  const selected = visible.find(o => o.id === selectedId)
    || (selectedId == null ? visible[0] : null);
  const selectedMissing = selectedId != null && !selected;

  // 선택한 기관의 소속 케어매니저 현황. 상세 패널이 열릴 때마다 해당 기관으로
  // 좁혀 조회(care_managers index가 organization_id 필터·권한 스코프를 이미 지원).
  // null=미조회/로딩, 배열=결과. 권한 없는 역할은 빈 배열로 떨어져 자연히 비표시.
  const [orgManagers, setOrgManagers] = React.useState(null);
  // 매니저가 많은 기관용 빠른 검색어. 기관을 바꾸면 초기화한다.
  const [mgrQuery, setMgrQuery] = React.useState("");
  const selectedOrgId = selected?.id ?? null;
  React.useEffect(() => {
    if (selectedOrgId == null) { setOrgManagers(null); return; }
    let alive = true;
    setOrgManagers(null);
    setMgrQuery("");
    api.list("care_managers", { organization_id: String(selectedOrgId) })
      .then(list => { if (alive) setOrgManagers(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setOrgManagers([]); });
    return () => { alive = false; };
  }, [selectedOrgId]);

  // 헤더 상태 요약(가용 N) + 검색 적용된 표시 목록. 목록이 길어도 한눈에
  // 가용 인원을 파악하고, 8명 초과면 이름·자격으로 좁혀 찾는다.
  const mgrAvailable = React.useMemo(
    () => Array.isArray(orgManagers) ? orgManagers.filter(m => m.status === "available").length : 0,
    [orgManagers]);
  const mgrFiltered = React.useMemo(() => {
    if (!Array.isArray(orgManagers)) return [];
    const q = mgrQuery.trim().toLowerCase();
    if (!q) return orgManagers;
    return orgManagers.filter(m =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.qualification || "").toLowerCase().includes(q));
  }, [orgManagers, mgrQuery]);

  // bulk 선택 — Set으로 페이지 간 일관 유지. 페이지에서 사라진 id는 그대로 유지(다음 페이지로 가도 선택 유지).
  // 다른 필터/페이지 변경 후 한 번에 적용 가능.
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const visibleIds = React.useMemo(() => new Set(visible.map(o => o.id)), [visible]);
  const visibleSelectedCount = React.useMemo(() => {
    let n = 0;
    for (const id of selectedIds) if (visibleIds.has(id)) n++;
    return n;
  }, [selectedIds, visibleIds]);
  const allVisibleSelected = visible.length > 0 && visibleSelectedCount === visible.length;

  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach(o => next.delete(o.id));
      else                    visible.forEach(o => next.add(o.id));
      return next;
    });
  };
  const clearBulkSelection = () => setSelectedIds(new Set());

  // bulk 액션. 단일 fetch로 트랜잭션 처리 → 성공 시 reload.
  const bulkUpdate = async (update) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (!confirm(`선택한 ${ids.length}개 기관을 일괄 변경합니다.\n진행할까요?`)) return;
    try {
      const r = await fetch("/api/v1/organizations/bulk_update", {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ids, update }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(apiErrorMessage(j, r.status)); return; }
      clearBulkSelection();
      await load();
    } catch (e) { alert(humanizeError(e)); }
  };
  const bulkDestroy = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (!confirm(`선택한 ${ids.length}개 기관을 삭제합니다.\n소속 케어매니저가 있는 기관은 삭제되지 않으니 먼저 다른 기관으로 이관하세요.\n삭제는 되돌릴 수 없습니다.\n\n진행할까요?`)) return;
    try {
      const r = await fetch("/api/v1/organizations/bulk_destroy", {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ids }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(apiErrorMessage(j, r.status)); return; }
      clearBulkSelection();
      await load();
    } catch (e) { alert(humanizeError(e)); }
  };

  const clearAllFilters = () => {
    setQ("");
    setCatFilter("all");
    setContractFilter("all");
    setRegionFilter("all");
  };

  const openNew  = () => {
    // 권역 한정 역할은 자기 권역으로 고정 생성(백엔드도 region_id 강제). 폼 기본
    // region_id를 본인 권역으로 채워 지도 센터도 해당 권역으로 잡히게 한다.
    setForm({ ...EMPTY_ORG, region_id: regionLocked ? String(user.region_id) : "" });
    setCandidates([]); // 신규 기관은 아직 소속 계정이 없다 → 자유 입력만.
    setEditing("new"); setErr(null);
  };
  const openEdit = async (o) => {
    setForm({ ...o }); setEditing(o.id); setErr(null); setCandidates([]);
    // 대표 담당자 후보(이 기관 소속 계정) 로드 — 셀렉트가 비면 자유 입력 폴백.
    try {
      const r = await fetch(`/api/v1/organizations/${o.id}/contact_candidates`, { credentials: "same-origin" });
      if (r.ok) setCandidates(await r.json());
    } catch { /* 후보 못 불러오면 자유 입력으로 동작 */ }
  };
  const close    = () => { setEditing(null); setErr(null); };

  const save = async () => {
    // 등록 시 좌표 필수(매칭 거리 기준점). 편집은 강제하지 않되, 값이 있으면 범위 검증.
    if (editing === "new" && (form.latitude == null || form.longitude == null)) {
      setErr("기관 위치를 지도에서 지정해주세요.");
      return;
    }
    if (form.latitude != null && (form.latitude < -90 || form.latitude > 90)) {
      setErr("위도는 -90~90 범위여야 합니다.");
      return;
    }
    if (form.longitude != null && (form.longitude < -180 || form.longitude > 180)) {
      setErr("경도는 -180~180 범위여야 합니다.");
      return;
    }
    try {
      const payload = pick(form, editing === "new" ? NEW_FIELDS : EDIT_FIELDS);
      // region_id 빈 문자열 → null (Rails가 nil로 받아 NULL 저장).
      if (payload.region_id === REGION_NONE) payload.region_id = null;
      else if (payload.region_id != null)    payload.region_id = Number(payload.region_id);
      if (editing === "new") await api.create("organizations", payload);
      else                    await api.update("organizations", editing, payload);
      await load();
      close();
      notifySaved();
    } catch (e) { setErr(humanizeError(e)); }
  };

  // OrganizationStatsRefreshJob을 **백그라운드 큐잉**(서버가 즉시 202 반환). 기관이
  // 수만 개라 인라인 집계는 타임아웃이 나므로 비동기로 돌린다. 집계는 백그라운드에서
  // 진행되어 즉시 목록에 반영되지 않으므로, 큐잉 안내만 하고 사용자가 나중에 새로고침한다.
  const refreshStats = async () => {
    setRefreshing(true);
    const startedAt = Date.now();
    try {
      const r = await fetch("/api/v1/organizations/refresh_stats", {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert(apiErrorMessage(j, r.status)); return; }

      // 집계는 백그라운드(비동기)에서 돈다. 버튼은 "집계 중…"을 유지하면서, 완료될 때까지
      // 가볍게 폴링한다 — 아무 기관의 stats_refreshed_at가 클릭 시각 이후로 갱신되면(=집계가
      // 전 기관 타임스탬프를 한 번에 update) 완료로 보고 목록을 새로 받아 화면에 반영한다.
      // (예전엔 매번 "백그라운드에서 시작" 안내만 떠 완료가 화면에 안 보였다.)
      const deadline = startedAt + 15000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(res => setTimeout(res, 1200));
        try {
          const cr = await fetch("/api/v1/organizations?page=1&per_page=1", { credentials: "same-origin" });
          const cd = await cr.json().catch(() => ({}));
          const list = Array.isArray(cd) ? cd : (cd.organizations || []);
          const stamp = list[0]?.stats_refreshed_at ? new Date(list[0].stats_refreshed_at).getTime() : 0;
          if (stamp >= startedAt) done = true;
        } catch { /* 일시 오류는 무시하고 계속 폴링 */ }
      }
      await load(); // 최신 통계/타임스탬프로 목록 갱신
      notifySaved(done
        ? "통계 집계가 완료되어 목록을 갱신했습니다."
        : "집계가 백그라운드에서 진행 중입니다. 잠시 후 다시 새로고침해 주세요.");
    } catch (e) { alert(humanizeError(e)); } finally { setRefreshing(false); }
  };

  // 모든 기관의 stats_refreshed_at 중 가장 최신 — 헤더 라벨용.
  const lastRefreshedAt = React.useMemo(() => {
    const stamps = orgs.map(o => o.stats_refreshed_at).filter(Boolean).map(s => new Date(s).getTime());
    return stamps.length ? new Date(Math.max(...stamps)).toISOString() : null;
  }, [orgs]);

  const askDelete = async (o) => {
    setDeletePrompt({ org: o, impact: null, loading: true });
    try {
      const res = await fetch(`/api/v1/organizations/${o.id}/deletion_impact`, { credentials: "same-origin" });
      const j = await res.json().catch(() => ({}));
      setDeletePrompt({ org: o, impact: j.impact || {}, loading: false });
    } catch { setDeletePrompt({ org: o, impact: {}, loading: false }); }
  };
  const cancelDelete  = () => setDeletePrompt(null);
  const confirmDelete = async () => {
    if (!deletePrompt) return;
    try {
      await api.remove("organizations", deletePrompt.org.id);
      setDeletePrompt(null);
      await load();
    } catch (e) { alert(humanizeError(e)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // 카드/헤더 카운트는 서버의 facet 결과 사용 — 페이지·필터 무관 정확.
  // totals/contractedCount은 전체 인벤토리 기준이라 검색 q는 반영되지만 페이지 제한 X.
  const totals = counts.total;
  const contractedCount = counts.by_contract?.completed || 0;

  // 유관기관(agency) 담당자는 '여러 기관 관리'가 아니라 '자기 기관 관리'가 역할이므로
  // 다기관 표/필터 대신 단일 기관 전용 화면으로 분기한다. (hook은 위에서 전부 호출되어
  // 순서가 보존되므로, 모든 hook 이후의 조건부 return은 안전하다.)
  if (userLoaded && user?.role === "agency") {
    return <AgencyOrgManagement/>;
  }

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="org"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar
            title={
              <span style={{display:'inline-flex', alignItems:'center', gap:8}}>
                유관기관 관리
                {regionLocked && (
                  <span title="현재 보고 있는 권역" style={{
                    fontSize:12, fontWeight:600, color:'#E8825B', background:'#FBEDE4',
                    padding:'2px 9px', borderRadius:999, lineHeight:1.6,
                  }}>{user.region_name}</span>
                )}
              </span>
            }
            sub={`등록 ${totals.toLocaleString()}곳 · 협약 ${contractedCount.toLocaleString()}곳`
                 + (geoOnly ? " · 좌표 있는 기관만 보는 중" : "")
                 + (catFilter !== "all" ? ` · 카테고리 필터: ${CAT_LABEL[catFilter]}` : "")
                 + (serviceFilter ? ` · 급여 필터: ${serviceFilter.label}` : "")}/>
          <div className="main">

            {/* Category cards (DB-driven counts) — click to filter the table. */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:14}}>
              {[
                {key:'hospital', l:'병원·의원',     i:'heart'},
                {key:'health',   l:'보건소·지소',   i:'check'},
                {key:'fire',     l:'소방·119',     i:'alert'},
                {key:'welfare',  l:'복지관·주민센터', i:'org'},
                {key:'care',     l:'요양·재가기관', i:'org'},
              ].map((c,i)=>{
                const cnt = counts.by_category?.[c.key] || 0;
                const recent = counts.recent_by_category?.[c.key] || 0;
                const window = counts.recent_window_days || 30;
                const color = CAT_COLOR[c.key];
                const active = catFilter === c.key;
                // 트렌드 칩: 최근 N일 내 신규가 1건이라도 있으면 녹색 + 표시.
                // 없으면 변동 없음을 의미하는 회색 점 — 0을 노골적으로 안 보여 시각 잡음 줄임.
                return (
                  <div key={c.key} role="button" tabIndex={0}
                    onClick={()=>setCatFilter(active ? "all" : c.key)}
                    onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCatFilter(active ? "all" : c.key); } }}
                    aria-pressed={active}
                    title={active ? "필터 해제" : `${c.l} 카테고리만 보기 · 최근 ${window}일 신규 ${recent}곳`}
                    className="card card-pad"
                    style={{padding:16, cursor:'pointer',
                            border: active ? `1.5px solid var(--${color}-500)` : '1px solid var(--border-soft)',
                            background: active ? `var(--${color}-50)` : 'var(--surface)',
                            transition: 'border-color 0.15s, background 0.15s'}}>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                      <span style={{width:34, height:34, borderRadius:10, background:`var(--${color}-50)`, color:`var(--${color}-${color==='amber'?'500':'600'})`, display:'grid', placeItems:'center'}}>
                        <span style={{width:18, height:18, display:'inline-block'}}>{Ic[c.i]}</span>
                      </span>
                      {recent > 0 ? (
                        <span style={{fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:6,
                                       background:'var(--green-50)', color:'var(--green-600)'}}
                              aria-label={`최근 ${window}일 내 신규 등록 ${recent}곳`}>
                          ↑ {recent} · {window}일
                        </span>
                      ) : (
                        <span className="muted" style={{fontSize:10}}
                              aria-label={`최근 ${window}일 내 신규 등록 없음`}>
                          — {window}일
                        </span>
                      )}
                    </div>
                    <div style={{fontSize:13, fontWeight:700, marginTop:10}}>{c.l}</div>
                    <div style={{display:'flex', alignItems:'baseline', gap:6, marginTop:2}}>
                      <div style={{fontSize:22, fontWeight:800}}>{cnt}</div>
                      <div className="muted" style={{fontSize:11}}>등록</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card" style={{padding:14, marginBottom:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
              <div className="searchbar" style={{flex:'0 0 260px', position:'relative'}}>
                <span style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--ink-3)'}}>{Ic.search}</span>
                <input className="input" style={{paddingLeft:36}} placeholder="기관명·주소·담당자 검색" value={q} onChange={e=>setQ(e.target.value)}/>
              </div>
              {/* 카테고리 필터는 상단 5장 카드가 단일 진입점.
                  중복 셀렉트는 의도적으로 제거 — 카드의 active/색 상태가 현재 카테고리를
                  시각적으로 더 명확히 드러내고, 다른 필터(협약/권역)도 같은 필터바에서
                  추가 노이즈 없이 한 줄에 정렬된다.
                  카드 클릭 → 토글, 활성 카드 재클릭 → 해제. */}
              <select className="select" value={contractFilter} onChange={e=>setContractFilter(e.target.value)} style={{width:120}}>
                <option value="all">전체 협약</option>
                <option value="completed">완료</option>
                <option value="reviewing">검토중</option>
                <option value="linked">연동중</option>
                <option value="none">미체결</option>
              </select>
              {!regionLocked && (
                <select className="select" value={regionFilter} onChange={e=>setRegionFilter(e.target.value)} style={{width:160}}>
                  <option value="all">전체 권역</option>
                  <option value="none">권역 미지정</option>
                  {regions.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
                </select>
              )}
              <select className="select" value={sortKey} onChange={e=>setSortKey(e.target.value)} style={{width:160}}>
                {SORT_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              {serviceFilter && (
                <span className="badge badge-sky" style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:11.5}}
                      title={`${serviceFilter.label}(${serviceFilter.code})을 제공하는 기관만 보는 중`}>
                  급여 {serviceFilter.label}
                  <button type="button" aria-label="급여 필터 해제"
                    onClick={()=>{
                      setServiceFilter(null);
                      // 뒤로가기로 다시 필터가 살아나지 않게 주소창도 정리.
                      const url = new URL(window.location.href);
                      url.searchParams.delete("service_code");
                      url.searchParams.delete("service_label");
                      window.history.replaceState({}, "", url.toString());
                    }}
                    style={{background:'transparent', border:'none', padding:0, cursor:'pointer', color:'inherit', fontWeight:800, lineHeight:1}}>×</button>
                </span>
              )}
              {/* 좌표 보유 기관만 — 지도에 표시 가능한 기관만 추린다(서버 geo=1). */}
              <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12.5, color:'var(--ink-2)', cursor:'pointer', whiteSpace:'nowrap'}}
                     title="위경도 좌표가 있는(지도 표시 가능) 기관만 표시">
                <input type="checkbox" checked={geoOnly} onChange={e=>setGeoOnly(e.target.checked)} style={{cursor:'pointer'}}/>
                좌표 있는 기관만
              </label>
              <span className="muted" style={{fontSize:11.5, marginLeft:8}}>
                {pagination.total > 0 ? <>필터 적용 {pagination.total.toLocaleString()}건 · 페이지 {pagination.page}/{pagination.total_pages}</> : `${visible.length}건`}
              </span>
              <div style={{flex:1}}/>
              <span className="muted" style={{fontSize:11, marginRight:4}} title={lastRefreshedAt || ""}>
                통계 갱신: <b style={{color:'var(--ink-2)'}}>{formatRefreshedAt(lastRefreshedAt)}</b>
              </span>
              {isSystemAdmin && (
                <button className="btn btn-ghost btn-md" onClick={refreshStats} disabled={refreshing}>
                  {refreshing ? '집계 중…' : '통계 새로고침'}
                </button>
              )}
              {perms.canExportOrgs && (
                <a className="btn btn-ghost btn-md" href="/api/v1/organizations/export.csv" style={{textDecoration:'none'}}>{Ic.arrow} CSV</a>
              )}
              {perms.canManageOrganizations && (
                <button className="btn btn-primary btn-md" onClick={openNew}>{Ic.plus} 기관 등록</button>
              )}
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 380px', gap:14}}>
              <div className="card" style={{overflow:'hidden'}}>
                <table className="tbl">
                  <thead><tr>
                    {isSystemAdmin && (
                      <th style={{width:40, paddingLeft:14}}>
                        <input type="checkbox" checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          title="페이지 전체 선택/해제"/>
                      </th>
                    )}
                    <th style={{paddingLeft: isSystemAdmin ? 6 : 22}}>기관명</th>
                    <th>카테고리</th><th>주소</th><th>담당자</th><th>협약</th><th>케이스</th><th>응답</th><th/>
                  </tr></thead>
                  <tbody>
                  {loading && <tr><td colSpan={isSystemAdmin ? 9 : 8} className="muted" style={{padding:30, textAlign:'center'}}>불러오는 중…</td></tr>}
                  {visible.map(o=>(
                    <tr key={o.id} style={{background: selectedId===o.id?'var(--orange-50)':'transparent', cursor:'pointer'}} onClick={()=>setSelectedId(o.id)}>
                      {isSystemAdmin && (
                        <td style={{paddingLeft:14}} onClick={e=>e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(o.id)}
                            onChange={()=>toggleSelectId(o.id)}/>
                        </td>
                      )}
                      <td style={{paddingLeft: isSystemAdmin ? 6 : 22}}>
                        <div style={{display:'flex', alignItems:'center', gap:10}}>
                          <span style={{width:30, height:30, borderRadius:8, background:`var(--${CAT_COLOR[o.category]}-50)`, color:`var(--${CAT_COLOR[o.category]}-${CAT_COLOR[o.category]==='amber'?'500':'600'})`, display:'grid', placeItems:'center'}}>
                            <span style={{width:14, height:14, display:'inline-block'}}>{Ic.org}</span>
                          </span>
                          <b>{o.name}</b>
                        </div>
                      </td>
                      <td><span className={"badge badge-" + CAT_COLOR[o.category]}>{CAT_LABEL[o.category]}</span></td>
                      <td className="muted">{o.address}</td>
                      <td><ContactInline contact={o.contact}/></td>
                      <td><span className={"badge badge-" + CONTRACT_COLOR[o.contract_status]}>{CONTRACT_LABEL[o.contract_status]}</span></td>
                      <td className="mono">
                        {o.case_count}
                        <TrendPill trend={o.trends_30d?.case_count}/>
                      </td>
                      <td className="mono muted">
                        {formatResponseTime(o.avg_response_mins)}
                        <TrendPill trend={o.trends_30d?.avg_response_mins} inverted fmt={v => formatResponseTime(v)}/>
                      </td>
                      <td style={{whiteSpace:'nowrap'}}>
                        {perms.canManageOrganizations && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); openEdit(o);}}>편집</button>
                        )}
                        {perms.canManageOrganizations && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); askDelete(o);}} style={{color:'var(--rose-500)'}}>삭제</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
                {pagination.total_pages > 1 && (
                  <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end'}}>
                    <Pager page={pagination.page} totalPages={pagination.total_pages} onPage={setPage}/>
                  </div>
                )}
              </div>

              {/* Detail panel from DB */}
              <div className="card" style={{overflow:'hidden'}}>
                {selected ? (
                  <>
                    <div style={{height:120, background:`linear-gradient(135deg, var(--${CAT_COLOR[selected.category]}-50), var(--orange-50))`, display:'grid', placeItems:'center'}}>
                      <span style={{width:54, height:54, borderRadius:14, background:'#fff', color:`var(--${CAT_COLOR[selected.category]}-${CAT_COLOR[selected.category]==='amber'?'500':'600'})`, display:'grid', placeItems:'center', boxShadow:'var(--shadow-md)'}}>
                        <span style={{display:'grid', placeItems:'center', fontSize:28}}>{Ic.org}</span>
                      </span>
                    </div>
                    <div style={{padding:'18px 22px 8px'}}>
                      <div style={{display:'flex', alignItems:'center', gap:8}}>
                        <div className="h2" style={{fontSize:18}}>{selected.name}</div>
                        <span className={"badge badge-" + CAT_COLOR[selected.category]}>{CAT_LABEL[selected.category]}</span>
                      </div>
                      <div className="muted" style={{fontSize:12, marginTop:4}}>{selected.address}</div>
                    </div>
                    <div style={{padding:'12px 22px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
                      {[
                        {l:'대표 연락처', v:selected.phone||'-'},
                        {l:'소속 권역',   v:selected.region_name || '미지정'},
                        // 파일럿 시 단위 권역(김해·남양주)만 하위 구역을 갖는다. 없으면 '-'.
                        {l:'하위 구역',   v:selected.sub_area || '-'},
                        {l:'등록일', v:formatDate(selected.registered_at)},
                        {l:'협약일', v:formatDate(selected.contract_signed_at)},
                        {l:'응급 핫라인', v:selected.emergency_phone||'-', c:'var(--rose-500)'},
                        {l:'평균 응답', v: formatResponseTime(selected.avg_response_mins)},
                        {l:'담당자', v: <ContactInline contact={selected.contact}/>},
                        ...(selected.contact?.verified ? [
                          {l:'담당자 연락처', v: selected.contact.phone || '-'},
                          {l:'담당자 이메일', v: selected.contact.email || '-'},
                        ] : []),
                        {l:'담당자 나이', v: selected.contact_age ? `${selected.contact_age}세` : '-'},
                      ].map((f,i)=>(
                        <div key={i}>
                          <div className="muted" style={{fontSize:10, fontWeight:600}}>{f.l}</div>
                          <div style={{fontSize:12.5, fontWeight:600, color:f.c||'var(--ink-1)', marginTop:2}}>{f.v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'8px 22px 16px'}}>
                      <div className="muted" style={{fontSize:10, fontWeight:700, marginBottom:8, letterSpacing:'0.04em'}}>제공 서비스</div>
                      <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                        {(selected.service_tags||[]).map((s,i)=>(
                          <span key={i} style={{padding:'4px 10px', fontSize:11, fontWeight:600, background:'var(--bg-sub)', borderRadius:99, color:'var(--ink-2)'}}>{s}</span>
                        ))}
                        {(!selected.service_tags || selected.service_tags.length===0) && <span className="muted" style={{fontSize:11}}>등록된 서비스 없음</span>}
                      </div>
                    </div>
                    {/* 사업 정보 — 통합돌봄 자원은행 1층(무슨 사업을·누구에게·어떤 재원으로).
                        현재는 남양주 N-CARE 임포트 기관에만 채워져 있어, 값이 하나라도
                        있을 때만 블록을 노출한다(빈 라벨 나열 방지). */}
                    {(selected.program_purpose || selected.program_target || selected.funding_source || selected.budget_max) && (
                      <div style={{padding:'12px 22px 16px', borderTop:'1px solid var(--border-soft)'}}>
                        <div className="muted" style={{fontSize:10, fontWeight:700, marginBottom:8, letterSpacing:'0.04em'}}>사업 정보</div>
                        {selected.program_purpose && (
                          <div style={{marginBottom:8}}>
                            <div className="muted" style={{fontSize:10, fontWeight:600}}>사업 목적</div>
                            <div style={{fontSize:12, lineHeight:1.55, color:'var(--ink-1)', marginTop:2}}>{selected.program_purpose}</div>
                          </div>
                        )}
                        {selected.program_target && (
                          <div style={{marginBottom:8}}>
                            <div className="muted" style={{fontSize:10, fontWeight:600}}>지원 대상</div>
                            <div style={{fontSize:12, lineHeight:1.55, color:'var(--ink-1)', marginTop:2}}>{selected.program_target}</div>
                          </div>
                        )}
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
                          {selected.funding_source && (
                            <div>
                              <div className="muted" style={{fontSize:10, fontWeight:600}}>재원</div>
                              <div style={{fontSize:12, fontWeight:600, color:'var(--ink-1)', marginTop:2}}>{selected.funding_source}</div>
                            </div>
                          )}
                          {selected.budget_max && (
                            <div>
                              <div className="muted" style={{fontSize:10, fontWeight:600}}>
                                연간 예산{selected.budget_year ? ` (${selected.budget_year})` : ''}
                              </div>
                              <div style={{fontSize:12, fontWeight:600, color:'var(--ink-1)', marginTop:2}}>
                                {formatBudgetRange(selected.budget_min, selected.budget_max)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{padding:'12px 22px 16px', borderTop:'1px solid var(--border-soft)'}}>
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
                        <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em'}}>소속 케어매니저</div>
                        {Array.isArray(orgManagers) && orgManagers.length > 0 &&
                          <span className="muted" style={{fontSize:11, fontWeight:600}}>
                            {orgManagers.length}명{mgrAvailable > 0 && <span style={{color:'var(--green-600)'}}> · 가용 {mgrAvailable}</span>}
                          </span>}
                      </div>
                      {orgManagers == null ? (
                        <div className="muted" style={{fontSize:11.5}}>불러오는 중…</div>
                      ) : orgManagers.length === 0 ? (
                        <div className="muted" style={{fontSize:11.5}}>소속된 케어매니저가 없습니다.</div>
                      ) : (
                        <>
                          {/* 인원이 많으면(8명 초과) 이름·자격 빠른 검색 노출 */}
                          {orgManagers.length > 8 && (
                            <input
                              value={mgrQuery}
                              onChange={(e)=>setMgrQuery(e.target.value)}
                              placeholder="이름·자격으로 찾기"
                              className="input"
                              style={{width:'100%', fontSize:12, padding:'6px 10px', marginBottom:8}}
                            />
                          )}
                          {/* 고정 높이 스크롤 컨테이너 — 매니저가 많아도 패널이 끝없이
                              길어지지 않게 막고 아래 액션 버튼(편집/삭제)을 항상 화면에 남긴다. */}
                          <div style={{display:'flex', flexDirection:'column', gap:6, maxHeight:248, overflowY:'auto', paddingRight:2}}>
                            {mgrFiltered.map(m => (
                              <div key={m.id} style={{display:'flex', alignItems:'center', gap:8, padding:'7px 10px', background:'var(--bg-sub)', borderRadius:8}}>
                                <div style={{flex:1, minWidth:0}}>
                                  <div style={{fontSize:12.5, fontWeight:600, color:'var(--ink-1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                                    {m.name}
                                    {m.qualification && <span className="muted" style={{fontSize:11, fontWeight:500, marginLeft:6}}>{m.qualification}</span>}
                                  </div>
                                  <div className="muted" style={{fontSize:10.5, marginTop:1}}>
                                    담당 {m.assigned_cases ?? 0}건 · 주 {m.weekly_hours ?? 0}h
                                  </div>
                                </div>
                                <span className={"badge badge-" + (MGR_STATUS_COLOR[m.status] || 'gray')} style={{fontSize:10}}>
                                  {MGR_STATUS_LABEL[m.status] || m.status}
                                </span>
                              </div>
                            ))}
                            {mgrFiltered.length === 0 && (
                              <div className="muted" style={{fontSize:11.5, padding:'4px 2px'}}>검색 결과가 없습니다.</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {perms.canManageOrganizations && (
                      <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)'}}>
                        <button className="btn btn-ghost btn-md" style={{width:'100%'}}
                          onClick={()=>setContactXfer(selected)}
                          title="이 기관에 연결된 계정을 해제(기관에서 분리)하거나 다른 기관으로 이관">
                          담당자·계정 해제·이관{isSystemAdmin ? "" : " 신청"}
                        </button>
                      </div>
                    )}
                    <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:6, flexWrap:'wrap'}}>
                      <button className="btn btn-ghost btn-md" onClick={()=>setHistoryOrg(selected)}
                        title="운영 지표 30/90일 이력 보기">
                        이력
                      </button>
                      {isSystemAdmin && (
                        <button className="btn btn-ghost btn-md" onClick={()=>setMergeSource(selected)}
                          title="다른 기관으로 흡수">
                          병합
                        </button>
                      )}
                      {perms.canManageOrganizations ? (
                        <>
                          <button className="btn btn-primary btn-md" style={{flex:1}} onClick={()=>openEdit(selected)}>편집</button>
                          <button className="btn btn-ghost btn-md" onClick={()=>askDelete(selected)} style={{color:'var(--rose-500)'}}>삭제</button>
                        </>
                      ) : (
                        <div className="muted" style={{flex:1, textAlign:'center', fontSize:11, padding:'6px 0'}}>
                          편집 권한이 없습니다.
                        </div>
                      )}
                    </div>
                  </>
                ) : selectedMissing ? (
                  <div style={{padding:30, textAlign:'center'}}>
                    <div className="muted" style={{fontSize:12.5, lineHeight:1.6}}>
                      선택한 기관이 현재 필터·페이지에 보이지 않습니다.<br/>
                      다른 행을 클릭하거나 필터를 지워 다시 표시해주세요.
                    </div>
                    <div style={{marginTop:14, display:'flex', gap:6, justifyContent:'center'}}>
                      <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}>필터 지우기</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setSelectedId(null)}>선택 해제</button>
                    </div>
                  </div>
                ) : <div className="muted" style={{padding:30, textAlign:'center'}}>기관을 선택하세요</div>}
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      <Modal
        open={editing != null}
        title={editing === "new" ? "기관 등록" : `${form.name || ""} 편집`}
        onClose={close}
        width={580}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={close}>취소</button>
          <button className="btn btn-primary btn-md" onClick={save}>저장</button>
        </>}
      >
        {err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{err}</div>}
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
          <Field label="기관명"><input className="input" value={form.name} onChange={e=>set('name')(e.target.value)}/></Field>
          <Field label="카테고리">
            <select className="select" value={form.category} onChange={e=>set('category')(e.target.value)}>
              <option value="hospital">병원</option>
              <option value="health">보건</option>
              <option value="fire">소방</option>
              <option value="welfare">복지</option>
              <option value="care">요양</option>
            </select>
          </Field>
          <Field label="주소"><input className="input" value={form.address||''} onChange={e=>set('address')(e.target.value)}/></Field>
          <Field label="대표 전화"><PhoneInput value={form.phone||''} onChange={e=>set('phone')(e.target.value)}/></Field>
          <Field label="응급 핫라인"><PhoneInput value={form.emergency_phone||''} onChange={e=>set('emergency_phone')(e.target.value)}/></Field>
          <ContactEditor form={form} set={set} candidates={candidates}/>
          <Field label="협약 상태">
            <select className="select" value={form.contract_status} onChange={e=>set('contract_status')(e.target.value)}>
              <option value="completed">완료</option>
              <option value="reviewing">검토중</option>
              <option value="linked">연동중</option>
              <option value="none">미체결</option>
            </select>
          </Field>
          <Field label="소속 권역">
            {regionLocked ? (
              // 권역 관리자는 자기 권역으로 고정(서버 강제). 선택 UI 대신 읽기 전용 표시.
              <input className="input" value={user.region_name || "내 권역"} disabled
                title="권역 관리자는 자기 권역으로 고정됩니다"/>
            ) : (
              <select className="select" value={form.region_id ?? REGION_NONE}
                onChange={e=>set('region_id')(e.target.value)}>
                <option value={REGION_NONE}>— 권역 미지정 —</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
              </select>
            )}
          </Field>
          {editing !== "new" && <Field label="제공 서비스">
            <TagsInput value={form.services} onChange={v=>set('services')(v)}
              placeholder="응급실 입력 후 Enter"/>
          </Field>}
        </div>

        <div style={{marginTop:14}}>
          <div className="label" style={{marginBottom:6}}>
            기관 위치 (지도에서 지정){editing === "new" && <span style={{color:'var(--rose-500)'}}> *</span>}
          </div>
          {(() => {
            const reg = regions.find(r => String(r.id) === String(form.region_id));
            const regionCenter = reg && reg.center_latitude != null && reg.center_longitude != null
              ? { lat: reg.center_latitude, lng: reg.center_longitude } : null;
            const value = form.latitude != null && form.longitude != null
              ? { lat: form.latitude, lng: form.longitude } : null;
            return (
              <LocationPicker
                value={value}
                center={regionCenter}
                onChange={(c) => setForm(f => ({ ...f, latitude: c.lat, longitude: c.lng }))}
              />
            );
          })()}
          <div className="muted" style={{fontSize:10, marginTop:4}}>
            소속 매니저가 본인 좌표를 지정하지 않으면, 이 기관 위치가 매칭 거리의 기준점이 됩니다.
          </div>
        </div>

        {editing !== "new" && (
          <div style={{marginTop:18, padding:'14px 16px', background:'var(--bg-sub)', borderRadius:10}}>
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-2)', marginBottom:8, letterSpacing:'0.04em'}}>
              운영 통계 (자동 집계 · 편집 불가)
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:10, fontSize:12}}>
              <div><div className="muted" style={{fontSize:10, fontWeight:600}}>케이스 건수</div><div className="mono" style={{fontSize:13, fontWeight:700}}>{form.case_count||0}</div></div>
              <div><div className="muted" style={{fontSize:10, fontWeight:600}}>평균 응답</div><div className="mono" style={{fontSize:13, fontWeight:700}}>{formatResponseTime(form.avg_response_mins)}</div></div>
            </div>
          </div>
        )}
      </Modal>

      {deletePrompt && (() => {
        // backend deletion_impact가 contract_status / region_name 등 운영 컨텍스트도 같이 보내준다.
        // organization 객체에서 추가 정보를 꺼내 모달 안내 메시지를 강하게 보강.
        const ctx = deletePrompt.org;
        const activeCases     = deletePrompt.impact?.active_cases || 0;
        const linkedUsers     = deletePrompt.impact?.linked_users || 0;
        const activeManagers  = deletePrompt.impact?.care_managers || 0;
        const retiredManagers = deletePrompt.impact?.retired_managers || 0;
        const liveContract = ctx?.contract_status === "completed" || ctx?.contract_status === "linked";
        const note = [
          liveContract && "⚠ 현재 협약 상태가 '완료/연동중'인 활성 파트너입니다. 삭제 전 협약 종료 절차를 먼저 검토하세요.",
          activeManagers > 0 && `⚠ 재직 중인 케어매니저가 ${activeManagers}명 있어 삭제할 수 없습니다. 먼저 다른 기관으로 이관하세요.`,
          activeCases > 0 && `⚠ 소속 매니저들이 진행 중인 미종료 케이스가 ${activeCases}건 있습니다. 케이스는 삭제되지 않지만 매니저 분리 후 후속 처리 필요.`,
          linkedUsers > 0 && `⚠ 이 기관에 연결된 사용자 계정이 ${linkedUsers}개 있습니다(예: 유관기관 담당자). 계정 소속을 먼저 정리하지 않으면 삭제할 수 없습니다.`,
          retiredManagers > 0 && `퇴직 케어매니저 ${retiredManagers}명은 삭제를 막지 않습니다 — 소속이 자동 분리되고(미소속) 이력·계정은 보존됩니다.`,
        ].filter(Boolean).join("\n\n");
        return (
          <DeletionConfirmModal
            resourceLabel="기관"
            subject={{ name: ctx.name }}
            loading={deletePrompt.loading}
            impact={deletePrompt.impact ? {
              care_managers:    ["재직 케어매니저", activeManagers],
              retired_managers: ["퇴직(분리됨)",   retiredManagers],
              linked_users:     ["연결된 계정",     linkedUsers],
              active_cases:     ["미종료 케이스",   activeCases],
            } : null}
            detachedNote={note}
            onCancel={cancelDelete}
            onConfirm={confirmDelete}
          />
        );
      })()}

      {/* Bulk 액션 플로팅 바 — 1건 이상 선택 시 노출 (system_admin 전용) */}
      {isSystemAdmin && selectedIds.size > 0 && (
        <div style={{
          position:'fixed', left:'50%', bottom:24, transform:'translateX(-50%)',
          background:'var(--surface)', border:'1px solid var(--border-soft)',
          borderRadius:14, padding:'10px 14px', boxShadow:'var(--shadow-lg)',
          display:'flex', alignItems:'center', gap:10, zIndex:40,
        }}>
          <span style={{fontSize:12.5, fontWeight:700, color:'var(--ink-1)', paddingRight:6}}>
            <b style={{color:'var(--orange-600)'}}>{selectedIds.size}</b>건 선택됨
          </span>
          <select className="select" style={{width:160, fontSize:12, padding:'6px 10px'}}
            onChange={e => { const v = e.target.value; e.target.value = ""; if (v) bulkUpdate({ contract_status: v }); }}
            defaultValue="">
            <option value="" disabled>협약 상태 변경…</option>
            <option value="completed">완료</option>
            <option value="reviewing">검토중</option>
            <option value="linked">연동중</option>
            <option value="none">미체결</option>
          </select>
          <select className="select" style={{width:180, fontSize:12, padding:'6px 10px'}}
            onChange={e => {
              const v = e.target.value; e.target.value = "";
              if (v === "") return;
              bulkUpdate({ region_id: v === "none" ? "" : v });
            }}
            defaultValue="">
            <option value="" disabled>권역 변경…</option>
            <option value="none">권역 미지정</option>
            {regions.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={bulkDestroy}
            style={{color:'var(--rose-500)'}}>
            삭제
          </button>
          <button className="btn btn-ghost btn-sm" onClick={clearBulkSelection}>
            해제
          </button>
        </div>
      )}

      {historyOrg && (
        <OrganizationHistoryModal
          organization={historyOrg}
          onClose={()=>setHistoryOrg(null)}
        />
      )}

      {mergeSource && (
        <OrganizationMergeModal
          source={mergeSource}
          allOrganizations={orgs}
          onClose={()=>setMergeSource(null)}
          onMerged={()=>{ setMergeSource(null); setSelectedId(null); load(); notifySaved("병합되었습니다"); }}
        />
      )}

      {contactXfer && (
        <OrgContactTransferModal
          org={contactXfer}
          direct={isSystemAdmin}
          onClose={()=>setContactXfer(null)}
          onDone={(mode)=>{
            setContactXfer(null);
            if (isSystemAdmin) {
              // 즉시 처리 — 담당자 연결이 바뀌었으니 목록/상세 갱신.
              load();
              notifySaved(mode === "transfer" ? "담당자를 이관했습니다" : "담당자를 해제했습니다");
            } else {
              notifySaved(mode === "transfer" ? "이관 신청이 접수되었습니다" : "해제 신청이 접수되었습니다");
            }
          }}
        />
      )}

      {saveToast}
    </div>
  );
}
// 표 셀 인라인 trend pill — 30일 전 대비 delta. delta=0/없음이면 숨김.
// inverted=true면 감소가 good(예: 응답시간 단축).
function TrendPill({ trend, inverted, fmt }) {
  if (!trend || trend.delta == null || trend.delta === 0) return null;
  const positive = inverted ? trend.delta < 0 : trend.delta > 0;
  const arrow = trend.delta > 0 ? "▲" : "▼";
  const color = positive ? "var(--green-600)" : "var(--rose-500)";
  const value = Math.abs(trend.delta);
  const shown = fmt ? fmt(value) : value;
  return (
    <div className="mono" style={{fontSize:9.5, color, fontWeight:700, marginTop:1}}
         title={`30일 전 대비 (${trend.past_recorded_on})`}>
      {arrow}{shown}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// 유관기관(agency) 담당자 전용 — '내 기관 관리' 단일 기관 화면.
// 백엔드 scoped_orgs가 agency를 자기 기관 1건으로 좁히고, 수정은 연락/담당자/주소/
// 위치/서비스 등 안전 필드만 허용(카테고리·권역·협약·통계는 운영자 전용 = 읽기).
function AgencyOrgManagement() {
  const [org, setOrg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadErr, setLoadErr] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState(null);
  const [candidates, setCandidates] = React.useState([]); // 대표 담당자 후보 계정
  const [err, setErr] = React.useState(null);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const { notifySaved, saveToast } = useSaveToast();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      // 이 화면은 "내 기관" 1건만 쓴다(서버가 agency 를 소속 기관으로 스코프).
      // 예전엔 페이지 파라미터 없이 불러 전체 기관을 직렬화시켰다 — 전국 데이터
      // 적재 후엔 그 응답만으로 수 초가 걸렸다.
      const r = await fetch("/api/v1/organizations?page=1&per_page=1", { credentials: "same-origin" });
      if (!r.ok) throw new Error(`오류 (${r.status})`);
      const data = await r.json();
      const list = Array.isArray(data) ? data : (data.organizations || []);
      setOrg(list[0] || null);
    } catch (e) { setLoadErr(apiErrorMessage(e)); } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const openEdit = async () => {
    setForm({ ...org }); setErr(null); setEditing(true); setCandidates([]);
    try {
      const r = await fetch(`/api/v1/organizations/${org.id}/contact_candidates`, { credentials: "same-origin" });
      if (r.ok) setCandidates(await r.json());
    } catch { /* 후보 못 불러오면 자유 입력으로 동작 */ }
  };
  const close    = () => { setEditing(false); setErr(null); };
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.latitude != null && (form.latitude < -90 || form.latitude > 90)) { setErr("위도는 -90~90 범위여야 합니다."); return; }
    if (form.longitude != null && (form.longitude < -180 || form.longitude > 180)) { setErr("경도는 -180~180 범위여야 합니다."); return; }
    try {
      const payload = pick(form, ["address","phone","emergency_phone","contact_name","contact_title","contact_age","primary_contact_user_id","services","latitude","longitude"]);
      await api.update("organizations", org.id, payload);
      await load();
      close();
      notifySaved();
    } catch (e) { setErr(humanizeError(e)); }
  };

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="agency" active="org"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="내 기관 관리" sub="소속 유관기관의 연락·운영 정보를 관리합니다"/>
          <div className="main">
            {loading ? (
              <div className="muted" style={{padding:60, textAlign:'center'}}>불러오는 중…</div>
            ) : loadErr ? (
              <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'14px 16px', borderRadius:10, fontSize:13}}>{loadErr}</div>
            ) : !org ? (
              <div className="muted" style={{padding:60, textAlign:'center'}}>소속 기관 정보를 찾을 수 없습니다. 운영자에게 문의해주세요.</div>
            ) : (
              <AgencyOrgCard org={org} onEdit={openEdit} onHistory={()=>setHistoryOpen(true)}/>
            )}
          </div>
          <AppFooter/>
        </div>
      </div>

      <Modal
        open={editing}
        title="내 기관 정보 수정"
        onClose={close}
        width={580}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={close}>취소</button>
          <button className="btn btn-primary btn-md" onClick={save}>저장</button>
        </>}
      >
        {form && <>
          {err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{err}</div>}
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
            <div style={{gridColumn:'1/-1'}}>
              <Field label="주소"><input className="input" value={form.address||''} onChange={e=>set('address')(e.target.value)}/></Field>
            </div>
            <Field label="대표 전화"><PhoneInput value={form.phone||''} onChange={e=>set('phone')(e.target.value)}/></Field>
            <Field label="응급 핫라인"><PhoneInput value={form.emergency_phone||''} onChange={e=>set('emergency_phone')(e.target.value)}/></Field>
            <ContactEditor form={form} set={set} candidates={candidates}/>
            <div style={{gridColumn:'1/-1'}}>
              <Field label="제공 서비스"><TagsInput value={form.services} onChange={v=>set('services')(v)} placeholder="응급실 입력 후 Enter"/></Field>
            </div>
          </div>

          <div style={{marginTop:14}}>
            <div className="label" style={{marginBottom:6}}>기관 위치 (지도에서 지정)</div>
            <LocationPicker
              value={form.latitude != null && form.longitude != null ? { lat: form.latitude, lng: form.longitude } : null}
              center={org && org.latitude != null && org.longitude != null ? { lat: org.latitude, lng: org.longitude } : null}
              onChange={(c) => setForm(f => ({ ...f, latitude: c.lat, longitude: c.lng }))}
            />
            <div className="muted" style={{fontSize:10, marginTop:4}}>
              소속 매니저가 본인 좌표를 지정하지 않으면, 이 기관 위치가 매칭 거리의 기준점이 됩니다.
            </div>
          </div>

          <div style={{marginTop:16, padding:'12px 14px', background:'var(--bg-sub)', borderRadius:10, fontSize:11.5, color:'var(--ink-2)', lineHeight:1.6}}>
            카테고리·소속 권역·협약 상태·운영 통계는 운영자가 관리하는 항목이라 여기서 수정할 수 없습니다.
          </div>
        </>}
      </Modal>

      {historyOpen && (
        <OrganizationHistoryModal organization={org} onClose={()=>setHistoryOpen(false)}/>
      )}
      {saveToast}
    </div>
  );
}

// 단일 기관 정보 카드 — agency '내 기관 관리' 본문. 정책/통계는 읽기 전용 표시.
function AgencyOrgCard({ org, onEdit, onHistory }) {
  const cat = CAT_LABEL[org.category] || org.category;
  const catColor = CAT_COLOR[org.category] || 'orange';
  const contract = CONTRACT_LABEL[org.contract_status] || org.contract_status || '미체결';
  const contractColor = CONTRACT_COLOR[org.contract_status] || 'rose';
  const tags = org.service_tags || [];
  const Row = ({ label, value }) => (
    <div style={{display:'flex', gap:12, padding:'10px 0', borderBottom:'1px solid var(--line)'}}>
      <div className="muted" style={{width:96, flexShrink:0, fontSize:12, fontWeight:600}}>{label}</div>
      <div style={{fontSize:13, fontWeight:600, color:'var(--ink-1)'}}>{value || <span className="muted" style={{fontWeight:400}}>미등록</span>}</div>
    </div>
  );
  return (
    <div style={{maxWidth:680, margin:'0 auto', display:'flex', flexDirection:'column', gap:16}}>
      {/* 헤더 카드 — 기관명 + 정책 배지(읽기 전용) */}
      <div className="card" style={{padding:'18px 20px'}}>
        <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12}}>
          <div>
            <div style={{fontSize:18, fontWeight:800, marginBottom:6}}>{org.name}</div>
            <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
              <span className="badge" style={{background:`var(--${catColor}-50)`, color:`var(--${catColor}-600)`, fontWeight:700}}>{cat}</span>
              <span className="badge" style={{background:`var(--${contractColor}-50)`, color:`var(--${contractColor}-600)`, fontWeight:700}}>협약 {contract}</span>
              {org.region_name && <span className="badge" style={{background:'var(--bg-sub)', color:'var(--ink-2)', fontWeight:700}}>{org.region_name}</span>}
            </div>
          </div>
          <div style={{display:'flex', gap:8, flexShrink:0}}>
            <button className="btn btn-ghost btn-md" onClick={onHistory}>이력</button>
            <button className="btn btn-primary btn-md" onClick={onEdit}>내 기관 정보 수정</button>
          </div>
        </div>
      </div>

      {/* 연락/담당자/위치 — 수정 가능 영역 */}
      <div className="card" style={{padding:'8px 20px 16px'}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.04em', padding:'12px 0 4px'}}>연락처 · 담당자</div>
        <Row label="주소" value={org.address}/>
        <Row label="대표 전화" value={org.phone}/>
        <Row label="응급 핫라인" value={org.emergency_phone}/>
        <Row label="담당자" value={org.contact?.name ? (
          <span style={{display:'inline-flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
            <ContactInline contact={org.contact}/>
            {org.contact.age ? <span className="muted" style={{fontWeight:400}}>({org.contact.age}세)</span> : null}
            {org.contact.verified && (org.contact.phone || org.contact.email) && (
              <span className="muted" style={{fontWeight:400, fontSize:12}}>· {[org.contact.phone, org.contact.email].filter(Boolean).join(' · ')}</span>
            )}
          </span>
        ) : null}/>
        <Row label="제공 서비스" value={tags.length ? (
          <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
            {tags.map((t,i) => <span key={i} className="badge" style={{background:'var(--bg-sub)', color:'var(--ink-2)'}}>{t}</span>)}
          </div>
        ) : null}/>
      </div>

      {/* 운영 통계 — 자동 집계(읽기 전용) */}
      <div className="card" style={{padding:'16px 20px'}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.04em', marginBottom:12}}>운영 통계 (자동 집계 · 편집 불가)</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:14}}>
          <div>
            <div className="muted" style={{fontSize:11, fontWeight:600}}>누적 케이스</div>
            <div className="mono" style={{fontSize:18, fontWeight:800}}>{org.case_count || 0}</div>
          </div>
          <div>
            <div className="muted" style={{fontSize:11, fontWeight:600}}>평균 응답</div>
            <div className="mono" style={{fontSize:18, fontWeight:800}}>{formatResponseTime(org.avg_response_mins)}</div>
          </div>
        </div>
        <div className="muted" style={{fontSize:10, marginTop:12}}>최근 집계: {formatRefreshedAt(org.stats_refreshed_at)}</div>
      </div>
    </div>
  );
}

export { OrgManagement };
